import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  BarChart3, TrendingUp, Users, Eye, Download,
  ArrowUpRight, FileText, Grid3X3, BookOpen, Activity,
} from 'lucide-react';
import AdminLayout from './AdminLayout';

const MetricCard = ({ label, value, change, icon: Icon, color, subtext }) => (
  <Card className="hover:shadow-md transition-all duration-200 hover:-translate-y-0.5">
    <CardContent className="p-5">
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 rounded-xl ${color} flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        {change !== undefined && (
          <span className="flex items-center gap-0.5 text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
            <ArrowUpRight className="w-3 h-3" />
            {change}%
          </span>
        )}
      </div>
      <p className="text-2xl font-bold text-gray-900 tabular-nums">{value}</p>
      <p className="text-sm text-gray-500 font-medium mt-0.5">{label}</p>
      {subtext && <p className="text-xs text-gray-400 mt-1">{subtext}</p>}
    </CardContent>
  </Card>
);

const ChartPlaceholder = ({ title, height = 'h-48' }) => (
  <Card>
    <CardHeader className="pb-2">
      <CardTitle className="text-sm font-semibold text-gray-700">{title}</CardTitle>
    </CardHeader>
    <CardContent>
      <div className={`${height} bg-gradient-to-b from-gray-50 to-gray-100 rounded-xl flex flex-col items-center justify-center gap-2 border border-gray-100`}>
        <BarChart3 className="w-8 h-8 text-gray-300" />
        <p className="text-xs text-gray-400">Chart data will appear here</p>
      </div>
    </CardContent>
  </Card>
);

const TablePlaceholder = ({ title, columns, rows = 5 }) => (
  <Card>
    <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
      <CardTitle className="text-sm font-semibold text-gray-700">{title}</CardTitle>
      <span className="text-xs text-gray-400">Last 30 days</span>
    </CardHeader>
    <CardContent className="pt-0">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-100">
            {columns.map(col => (
              <th key={col} className="pb-2 text-left text-xs font-semibold text-gray-500 tracking-wide">{col}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {[...Array(rows)].map((_, i) => (
            <tr key={i} className="border-b border-gray-50 last:border-0">
              {columns.map(col => (
                <td key={col} className="py-2.5 pr-4">
                  <div className="h-3 bg-gray-100 rounded animate-pulse" style={{ width: `${60 + Math.random() * 30}%` }} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </CardContent>
  </Card>
);

const AdminAnalytics = () => (
  <AdminLayout>
    <div className="space-y-6 w-full">

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="text-gray-500 text-sm mt-0.5">Platform performance and usage insights.</p>
        </div>
        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-4 py-2">
          <Activity className="w-4 h-4 text-violet-500" />
          <span className="text-sm text-gray-600 font-medium">Last 30 days</span>
        </div>
      </div>

      {/* API Notice */}
      <div className="rounded-xl border-2 border-dashed border-violet-200 bg-violet-50/50 px-5 py-4 flex items-center gap-4">
        <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center flex-shrink-0">
          <BarChart3 className="w-5 h-5 text-violet-600" />
        </div>
        <div>
          <p className="text-sm font-semibold text-violet-900">Analytics Dashboard</p>
          <p className="text-xs text-violet-600 mt-0.5">
            Connect to the analytics API to populate real-time data, charts, and usage reports below.
          </p>
        </div>
      </div>

      {/* Metric Cards — 4 columns */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard label="Template Uses" value="—" icon={TrendingUp} color="bg-violet-500" subtext="30 day period" />
        <MetricCard label="Active Users" value="—" icon={Users} color="bg-blue-500" subtext="Unique sessions" />
        <MetricCard label="Design Views" value="—" icon={Eye} color="bg-emerald-500" subtext="Public + private" />
        <MetricCard label="Downloads" value="—" icon={Download} color="bg-pink-500" subtext="All formats" />
      </div>

      {/* Charts Row — 2 columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartPlaceholder title="User Growth (30 days)" height="h-52" />
        <ChartPlaceholder title="Template Usage Over Time" height="h-52" />
      </div>

      {/* Wide chart */}
      <ChartPlaceholder title="Platform Activity — Designs Created vs Downloads" height="h-40" />

      {/* Tables Row — 2 columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TablePlaceholder
          title="Top Templates"
          columns={['Template', 'Category', 'Uses', 'Downloads']}
          rows={5}
        />
        <TablePlaceholder
          title="Top Elements"
          columns={['Element', 'Type', 'Uses', 'Likes']}
          rows={5}
        />
      </div>

      {/* Second tables row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <TablePlaceholder
          title="Most Active Designers"
          columns={['Designer', 'Submissions', 'Approved']}
          rows={4}
        />
        <TablePlaceholder
          title="Top Blog Posts"
          columns={['Title', 'Views', 'Reads']}
          rows={4}
        />
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-gray-700">Content Breakdown</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {[
              { label: 'Templates', icon: FileText, color: 'bg-violet-500', pct: 40 },
              { label: 'Elements', icon: Grid3X3, color: 'bg-blue-500', pct: 35 },
              { label: 'Blog Posts', icon: BookOpen, color: 'bg-emerald-500', pct: 25 },
            ].map(item => {
              const Icon = item.icon;
              return (
                <div key={item.label}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <div className={`w-5 h-5 rounded ${item.color} flex items-center justify-center`}>
                        <Icon className="w-3 h-3 text-white" />
                      </div>
                      <span className="text-xs font-medium text-gray-700">{item.label}</span>
                    </div>
                    <span className="text-xs text-gray-500 tabular-nums">{item.pct}%</span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${item.color} rounded-full opacity-60`}
                      style={{ width: `${item.pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>

    </div>
  </AdminLayout>
);

export default AdminAnalytics;
